import React, { createContext, useCallback, useMemo, useContext } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'

import {
  clearMutingIntention,
  setMutingIntention,
} from '../../../firebase/hooks/useMute'
import {
  useOnSpeakerAdded,
  useOnSpeakerUpdated,
  useOnSpeakerRemoved,
  useOnExpired,
} from '../messaging'
import useSpeakersQuery from '../../hooks/useSpeakersQuery'
import type {
  UseSpeakersQueryOptions,
  Speaker,
} from '../../hooks/useSpeakersQuery'
import { useUpdateSpeakerMutation } from '../../hooks/useUpdateSpeakerMutation'
import { type JoinToSpeakRequest } from '../types'
import { addSpeaker, updateSpeaker, removeSpeaker } from '../utils'

import type {
  JoinToSpeakHostContextValue,
  JoinToSpeakHostProviderProps,
} from './types'

const QUERY_KEY_PREFIX = 'speakers'

const JoinToSpeakHostContext =
  createContext<JoinToSpeakHostContextValue | null>(null)

const JoinToSpeakHostProvider = ({
  children,
}: JoinToSpeakHostProviderProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const getSpeakersQueryOptions = useCallback<() => UseSpeakersQueryOptions>(
    () => ({
      includeUnapproved: true,
      requestStatus: 'requested',
      creationSource: 'guest',
    }),
    []
  )
  const getQueryKey = useCallback(
    () => [QUERY_KEY_PREFIX, getSpeakersQueryOptions()],
    [getSpeakersQueryOptions]
  )
  const speakersQuery = useSpeakersQuery(getSpeakersQueryOptions())
  const updateSpeakerMutation = useUpdateSpeakerMutation()
  const invalidateSpeakersQueries = useCallback(
    () => queryClient.invalidateQueries(QUERY_KEY_PREFIX, { exact: false }),
    [queryClient]
  )

  const requests = useMemo<JoinToSpeakRequest[]>(() => {
    const speakers = speakersQuery.data ?? []

    return speakers.map<JoinToSpeakRequest>((speaker) => ({
      attendeeId: speaker.id,
      attendeeName: speaker.name,
      attendeeFullName: speaker.fullName,
      attendeeEmail: speaker.email,
      question: speaker.question ?? '',
      previewImageUrl: speaker.guestSpeakerPreviewLink,
    }))
  }, [speakersQuery.data])

  useOnSpeakerAdded(
    useCallback(
      async (message) => {
        if (message.speaker.requestStatus !== 'requested') {
          return
        }

        await queryClient.cancelQueries(getQueryKey())

        queryClient.setQueryData<Speaker[]>(
          getQueryKey(),
          (previousSpeakers = []) => {
            if (
              previousSpeakers.find(
                (speaker) => speaker.id === message.speaker.id
              ) !== undefined
            ) {
              return updateSpeaker(
                previousSpeakers,
                (speaker) => speaker.id === message.speaker.id,
                message.speaker
              )
            }

            return addSpeaker(previousSpeakers, message.speaker)
          }
        )
      },
      [queryClient, getQueryKey]
    )
  )

  useOnSpeakerUpdated(
    useCallback(
      async (message) => {
        if (message.speaker.requestStatus !== 'requested') {
          return
        }

        await queryClient.cancelQueries(getQueryKey())

        queryClient.setQueryData<Speaker[]>(
          getQueryKey(),
          (previousSpeakers = []) => {
            if (
              previousSpeakers.find(
                (speaker) => speaker.id === message.speaker.id
              ) !== undefined
            ) {
              return updateSpeaker(
                previousSpeakers,
                (speaker) => speaker.id === message.speaker.id,
                message.speaker
              )
            }

            return addSpeaker(previousSpeakers, message.speaker)
          }
        )
      },
      [queryClient, getQueryKey]
    )
  )

  useOnSpeakerRemoved(
    useCallback(
      async (message) => {
        await queryClient.cancelQueries(getQueryKey())

        queryClient.setQueryData<Speaker[]>(
          getQueryKey(),
          (previousSpeakers = []) =>
            removeSpeaker(
              previousSpeakers,
              (speaker) => speaker.id === message.speakerId
            )
        )
      },
      [queryClient, getQueryKey]
    )
  )

  useOnExpired(() => {
    invalidateSpeakersQueries()
    dispatch({
      type: 'SESSION-SIDEBAR-SPEAKERS:REFETCH',
    })
  })

  const handleAttendeePromotion = useCallback(
    async (attendeeId: string) => {
      await setMutingIntention({
        participantId: +attendeeId,
        isMuted: true,
        isPlaybackMuted: false,
      })

      await updateSpeakerMutation.mutateAsync({
        speakerId: attendeeId,
        requestStatus: 'approved',
      })

      await queryClient.cancelQueries(getQueryKey())

      queryClient.setQueryData<Speaker[]>(
        getQueryKey(),
        (previousSpeakers = []) =>
          removeSpeaker(
            previousSpeakers,
            (speaker) => speaker.id === attendeeId
          )
      )

      invalidateSpeakersQueries()
      dispatch({
        type: 'SESSION-SIDEBAR-SPEAKERS:REFETCH',
      })
    },
    [
      queryClient,
      dispatch,
      updateSpeakerMutation,
      getQueryKey,
      invalidateSpeakersQueries,
    ]
  )

  const handleAttendeeDenial = useCallback(
    async (attendeeId: string) => {
      await updateSpeakerMutation.mutateAsync({
        speakerId: attendeeId,
        requestStatus: 'denied',
      })

      await queryClient.cancelQueries(getQueryKey())

      queryClient.setQueryData<Speaker[]>(
        getQueryKey(),
        (previousSpeakers = []) =>
          removeSpeaker(
            previousSpeakers,
            (speaker) => speaker.id === attendeeId
          )
      )
    },
    [queryClient, updateSpeakerMutation, getQueryKey]
  )

  const handleAttendeeExpiration = useCallback(
    async (attendeeId: string) => {
      await updateSpeakerMutation.mutateAsync({
        speakerId: attendeeId,
        requestStatus: 'expired',
      })

      invalidateSpeakersQueries()
      clearMutingIntention(attendeeId)
      dispatch({
        type: 'SESSION-SIDEBAR-SPEAKERS:REFETCH',
      })
    },
    [updateSpeakerMutation, invalidateSpeakersQueries, dispatch]
  )

  const context = useMemo<JoinToSpeakHostContextValue>(
    () => ({
      requests,
      handleAttendeePromotion,
      handleAttendeeDenial,
      handleAttendeeExpiration,
    }),
    [
      requests,
      handleAttendeePromotion,
      handleAttendeeDenial,
      handleAttendeeExpiration,
    ]
  )

  return (
    <JoinToSpeakHostContext.Provider value={context}>
      {children}
    </JoinToSpeakHostContext.Provider>
  )
}

function useJoinToSpeakAsHost() {
  const context = useContext(JoinToSpeakHostContext)

  if (context === null) {
    throw new Error(
      'useJoinToSpeakAsHost must be used within a JoinToSpeakHostProvider'
    )
  }

  return context
}

export { JoinToSpeakHostProvider, useJoinToSpeakAsHost }
